// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * SponsoredLogos widget
 * BG-2676
 * 
 * This component displays a list of sponsored logos with optional navigation and filtering.
 * BG team requested this to be a separate widget from BrandGallery
 * This will be a new widget, 6 columns (to match the popular categories number),
 * with the allowance of as many rows as desired. Center the logos on a row that is not full.
 * For tablet displays, the layout adjusts to 4 logos per row, accompanied by an arrow for
 * scrolling. On mobile devices, the layout simplifies to a single logo per row with scrolling.
 */

import WidgetTitle from "@bg/App/Components/WidgetTitle";
import { CustomAppState } from "@bg/App/Store/Reducers";
import LazyImage, { LazyImageProps } from "@bg/MobiusOverrides/LazyImage";
import { styledSlider } from "@bg/XDL/Utilities";
import xScreen from "@bg/XDL/Utilities/xScreen";
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import Logger from "@insite/client-framework/Logger";
import { getBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import { getCategoriesDataView, getCategoryDepthLoaded, getCategoryState, getSubCategoryIds } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { FlexItemProps } from "@insite/content-library/Widgets/Brand/FlexItem";
import { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import Clickable from "@insite/mobius/Clickable";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import { LinkProps } from "@insite/mobius/Link";
import { MappedLink } from "@insite/mobius/Menu";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
// eslint-disable-next-line
import SwiperCore, { A11y, Navigation, Scrollbar } from "swiper";
// eslint-disable-next-line
import { Swiper, SwiperSlide } from "swiper/react";

SwiperCore.use([Navigation, Scrollbar, A11y]);

const enum fields {
    title = "title",
    brandsSelected = "brandsSelected",
    maximumBrandImageHeight = "maximumBrandImageHeight",
    displayBrandNames = "displayBrandNames",
    viewAllLinkTitle = "viewAllLinkTitle",
    viewAllLink = "viewAllLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.brandsSelected]: string[];
        [fields.maximumBrandImageHeight]: number;
        [fields.displayBrandNames]: boolean;
        [fields.viewAllLinkTitle]: string;
        [fields.viewAllLink]: LinkFieldValue;
    };
}

const getParameter = (categoryId: string, maxDepth: number) => ({
    maxDepth,
    startCategoryId: categoryId === emptyGuid ? undefined : categoryId,
    includeStartCategory: categoryId !== emptyGuid,
});

const setupCategoryLink = (
    state: CustomAppState,
    value: string,
    categoryIdsToLoad: Dictionary<number>,
    maxDepth: number,
    overrideTitle: string,
    categoryIdsCalled: string[],
) => {
    let mappedLink: MappedLink | undefined;
    
    if (!value) {
        return;
    }

    const depthLoaded = getCategoryDepthLoaded(state, value);
    const subCategoryIds = getSubCategoryIds(state, value);
    if (
        (depthLoaded < maxDepth || (!subCategoryIds && !categoryIdsCalled?.includes(value))) &&
        !getCategoriesDataView(state, getParameter(value, maxDepth)).isLoading
    ) {
        categoryIdsToLoad[value] = maxDepth;
    } else {
        if (value === emptyGuid) {
            mappedLink = {
                url: "",
                title: overrideTitle || translate("Products"),
            };
        } else {
            const categoryState = getCategoryState(state, value);
            if (!categoryState.value) {
                if (
                    !categoryState.isLoading &&
                    !getCategoriesDataView(state, getParameter(value, maxDepth)).isLoading
                ) {
                    categoryIdsToLoad[value] = maxDepth;
                }
                return;
            }
            mappedLink = {
                url: categoryState.value.path,
                title: overrideTitle || categoryState.value.shortDescription,
            };
        }

        loadSubCategories(mappedLink, subCategoryIds, 1, maxDepth, state);
    }

    return mappedLink;
};

const loadSubCategories = (
    mappedLink: MappedLink,
    subCategoryIds: readonly string[] | undefined,
    currentDepth: number,
    maxDepth: number,
    state: CustomAppState,
) => {
    if (currentDepth > maxDepth) {
        return;
    }
    mappedLink.children = [];
    if (!subCategoryIds) {
        return;
    }
    for (const subCategoryId of subCategoryIds) {
        const subCategory = getCategoryState(state, subCategoryId).value;
        if (!subCategory) {
            Logger.error(`Category ${subCategoryId} was not found in data state, excluding from navigation.`);
            continue;
        }
        const childLink: MappedLink = {
            url: subCategory.path,
            title: subCategory.shortDescription,
            children: [],
        };
        mappedLink.children.push(childLink);

        loadSubCategories(childLink, subCategory.subCategoryIds, currentDepth + 1, maxDepth, state);
    }
};

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const brandGalleryParameter = {
        brandIds: ownProps.fields.brandsSelected,
        randomize: false,
        pageSize: parseInt(`${ownProps.fields.brandsSelected?.length}` || "0", 10),
        select: "id,name,detailPagePath,logoSmallImagePath,logoAltText,name",
    };
    const brandsPageLink = getPageLinkByPageType(state, "BrandsPage");
    const brandsState = getBrandsDataView(state, brandGalleryParameter);
    const shouldLoadBrandGallery = !brandsState.isLoading && !brandsState.value;

    const {
        fields: { viewAllLink, viewAllLinkTitle },
    } = ownProps;

    const { type, value } = viewAllLink;

    let mappedLink: MappedLink | undefined;
    const categoryIdsToLoad: Dictionary<number> = {};
    const depthToLoad = 2;

    if (type === "Url") {
        mappedLink = {
            url: value,
            title: viewAllLinkTitle,
        };
    } else if (type === "Page") {
        const readonlyPageLink = getPageLinkByNodeId(state, value);
        if (readonlyPageLink) {
            mappedLink = { ...readonlyPageLink };
            if (viewAllLinkTitle) {
                mappedLink.title = viewAllLinkTitle;
            }
        }
    } else if (type === "Category") {
        mappedLink = setupCategoryLink(
            state,
            value,
            categoryIdsToLoad,
            depthToLoad,
            viewAllLinkTitle,
            state.data.categories.categoryIdsCalled,
        );
    }

    return {
        brandsState,
        shouldLoadBrandGallery,
        brandGalleryParameter,
        brandsPageLink,
        mappedLink,
        categoryIdsToLoad,
        categoryErrorStatusCodeById: state.data.categories.errorStatusCodeById,
        isMobileScreen: state.commonActions["isMobileScreen"],
    };
};

const mapDispatchToProps = {
    loadBrandGallery: loadBrands,
    loadCategories,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface BrandListStyles {
    titleText?: TypographyProps;
    container?: InjectableCss;
    imageContainer?: FlexWrapContainerProps;
    imageItem?: FlexItemProps;
    link?: LinkProps;
    image?: LazyImageProps;
    brandName?: TypographyProps;
    carouselWrapper?: InjectableCss;
    gridWrapper?: InjectableCss;
    swiperSlideWrapper?: InjectableCss;
}

export const listStyles: BrandListStyles = {
    titleText: {
        variant: "h3",
        transform: "inherit",
        css: css`
            border-width: 20px;
            text-align: center;
        `,
    },
    link: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            &:hover {
                > div {
                    filter: grayscale(0%);
                }
            }
            ${xScreen("gt","lg")} {
                width: calc(100% / 7);
            }
            ${xScreen("lte","md")} {
                width: calc(25% - 20px);
            }
            ${xScreen("lte","sm")} {
                width: 100%;
                min-height: 100px;
            }
        `,
    },
    imageItem: {
        css: css`
            padding: 5px;
            justify-content: center;
        `,
    },
    image: {
        css: css`
            width: 133px;
            height: 100%;
            filter: grayscale(100%);
        `,
    },
    brandName: {
        size: 15,
        weight: 500,
        lineHeight: "16px",
        color: "text",
        ellipsis: true,
        css: css`
            display: block;
            margin-top: 9px;
            padding: 0px 12px 20px 12px;
            text-align: center;
            opacity: 0.6;
            width: 100%;
        `,
    },
    carouselWrapper: {
        css: css`
            > div {
                display: flex;
                justify-content: center;
            }
            @media print {
                display: none;
            }
            ${xScreen("gt","lg")} {
                display: none;
            }
        `,
    },
    gridWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            gap: 30px;
            flex-wrap: wrap;
            ${xScreen("lte","md")} {
                display: none;
            }
        `,
    },
    swiperSlideWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 20px;
            width: 100%;
            ${xScreen("lte","sm")} {
                flex-direction: column;
                justify-content: center;
            }
        `,
    },
};

const styles = listStyles;

export const popularCategoriesContainer = css`
    ${styledSlider()}
    .swiper-container {
        width: 100% !important;
        padding: 15px;
        ${xScreen("lte","xs")} {
            height: 350px
        }
    }

    .swiper-slide {
        > div {
            box-shadow: unset;
        }
        display: flex;
        justify-content: center;
        @media screen and (min-width: 480px) {
            min-width: 133px;
        }
        ${xScreen("lte","xs")} {
            min-height: 350px;
        }
    }
`;

class SponsoredLogos extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        const { loadBrandGallery, shouldLoadBrandGallery, brandGalleryParameter } = this.props;
        if (shouldLoadBrandGallery) {
            loadBrandGallery(brandGalleryParameter);
        }
        this.loadCategoriesIfNeeded();
    }

    async componentDidMount() {
        // eslint-disable-next-line global-require
        const res = await require("swiper/swiper-bundle.min.css");
    }
    
    componentDidUpdate(prevProps: Readonly<Props>): void {
        this.loadCategoriesIfNeeded();
    }

    // Load category if needed to navigate to the view all link if it's authored as a category
    loadCategoriesIfNeeded() {
        const { categoryIdsToLoad } = this.props;
        for (const categoryId of Object.keys(categoryIdsToLoad)) {
            if (!this.props.categoryErrorStatusCodeById?.[categoryId]) {
                const maxDepth = categoryIdsToLoad[categoryId];
                this.props.loadCategories(getParameter(categoryId, maxDepth));
            }
        }
    }

    render() {
        const {
            brandsState: { isLoading, value: brandList },
            fields,
        } = this.props;

        if (isLoading || !brandList) {
            return null;
        }

        const imgProps = {
            style: {
                width: "auto",
                maxWidth: "100%",
                maxHeight: `${fields.maximumBrandImageHeight}px`,
            },
        };

        if (!brandList.length) {
            return null;
        }

        const slidesPerView = this.props.isMobileScreen ? 3 : 12;

        return (
            <>
                <WidgetTitle
                    title={fields.title}
                    viewAllText={fields.viewAllLinkTitle}
                    viewAllLink={this.props.mappedLink?.url}
                />
                <StyledWrapper {...styles.carouselWrapper}>
                    <StyledWrapper className="carousel-brands-container" css={popularCategoriesContainer}>
                        <Swiper
                            spaceBetween={30}
                            slidesPerView={1}
                            className="swiper-carousel"
                            navigation={{
                                nextEl: `.swiper-button-next.swiper-button-next${brandList[0].id}`,
                                prevEl: `.swiper-button-prev.swiper-button-prev${brandList[0].id}`,
                            }}
                            loop={brandList.length > slidesPerView}
                        >
                            {brandList.map((brand, index) => {
                                // Show 3 rows with 4 items per row (12 items in total)
                                if (index % slidesPerView === 0) {
                                    return (
                                        <SwiperSlide key={`grouped_slide_${index}`} className="swiper-slide">
                                            <StyledWrapper {...styles.swiperSlideWrapper}>
                                                {brandList.slice(index, index + slidesPerView).map((brandInRow, idx) => (
                                                    <Clickable  key={`brand_${brandInRow.id}`} href={brandInRow.detailPagePath} {...styles.link}>
                                                        <LazyImage
                                                            imgProps={imgProps}
                                                            {...styles.image}
                                                            src={brandInRow.logoSmallImagePath}
                                                            cloudinaryWidth="133"
                                                            altText={
                                                                brandInRow.logoAltText
                                                                    ? `${translate("Go to brand page")} ${brandInRow.logoAltText}`
                                                                    : `${translate("Go to brand page")} ${brandInRow.name}`
                                                            }
                                                        />
                                                        {fields.displayBrandNames && (
                                                            <Typography {...styles.brandName}>{brandInRow.name}</Typography>
                                                        )}
                                                    </Clickable>
                                                ))}
                                            </StyledWrapper>
                                        </SwiperSlide>
                                    );
                                }
                                return null;
                            })}
                        </Swiper>
                        <StyledWrapper className="swiper-navigation">
                            <div className={`swiper-button-prev swiper-button-prev${brandList[0].id}`}>
                                <ChevronLeft />
                            </div>
                            <div className={`swiper-button-next swiper-button-next${brandList[0].id}`}>
                                <ChevronRight />
                            </div>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.gridWrapper}>
                    {brandList.map(brand => (
                        <Clickable href={brand.detailPagePath} {...styles.link} key={`sponsored_logo_${brand.id}`}>
                            <LazyImage
                                imgProps={imgProps}
                                {...styles.image}
                                src={brand.logoSmallImagePath}
                                cloudinaryWidth="133"
                                altText={
                                    brand.logoAltText
                                        ? `${translate("Go to brand page")} ${brand.logoAltText}`
                                        : `${translate("Go to brand page")} ${brand.name}`
                                }
                            />
                            {fields.displayBrandNames && (
                                <Typography {...styles.brandName}>{brand.name}</Typography>
                            )}
                        </Clickable>
                    ))}
                </StyledWrapper>
            </>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SponsoredLogos),
    definition: {
        group: "Brands",
        icon: "image",
        displayName: "Sponsored Logos",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Our Brands",
                fieldType: "Contextual",
            },
            {
                name: fields.viewAllLinkTitle,
                displayName: "View All Link Title",
                editorTemplate: "TextField",
                defaultValue: "View All",
                fieldType: "Contextual",
            },
            {
                name: fields.viewAllLink,
                displayName: "View All Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Url",
                },
                fieldType: "Contextual",
            },
            {
                name: fields.brandsSelected,
                displayName: "Select brands",
                editorTemplate: "SelectBrandsField",
                defaultValue: [],
                fieldType: "Contextual",
                isRequired: true,
            },
            {
                name: fields.maximumBrandImageHeight,
                displayName: "Maximum brand image height",
                editorTemplate: "IntegerField",
                defaultValue: 100,
                fieldType: "Contextual",
                tooltip:
                    "By default, images will scale to meet the width of the screen for the number of brands to display per row. The maximum height value can be used to scale brand logos to be smaller. Default value is 100 pixels.",
            },
            {
                name: fields.displayBrandNames,
                displayName: "Display Brand Names",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;

